import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import * as PRODUCT_ONBOARDING from "constants/productOnboardings";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { EmptySalesTax } from "components/Illustrations/EmptySalesTax";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Button } from "components/DesignSystem/Button/Button";
import { ArrowDown } from "components/icons/ArrowDown";
import { PlusIcon } from "components/icons/PlusIcon";
import { AddExisting as AddExistingIcon } from "components/icons/AddExisting";
import classNames from "classnames";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { RegistrationTypeModal } from "components/RegistrationTypeModal/RegistrationTypeModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { AddRegistrationModal } from "./AddRegistrationModal";
import { AddExistingRegistrationModal } from "./AddExistingRegistrationModal";
import { OnboardingHomeCard } from "components/OnboardingHomeCard/OnboardingHomeCard";
import ConditionalToolTip from "components/design/conditionalToolTip";
import {
  Registration,
  RegistrationStatus,
  useGetSalesTaxRegistrationsQuery,
} from "store/apis/salesTaxRegistration";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { RegistrationDetails } from "./RegistrationDetails";
import {
  useCreateEntityProductOnboardingMutation,
  useGetEntityOnboardingProductsQuery,
} from "store/apis/productOnboarding";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { openOnboardingWidget } from "store/slices/onboardingWidget";
import { useDispatch } from "react-redux";
import { BusinessDetailsModal } from "./BusinessDetailsModal";
import { SalesTaxRegistrationPending } from "components/Illustrations/SalesTaxRegistrationPending";
import { useToast } from "hooks/useToast";
import {
  ANNUALLY,
  IN_REVIEW,
  MONTHLY,
  PENDING_DETAILS,
  PROCESSING,
  QUARTERLY,
  REGISTERED,
  SEMI_ANNUALLY,
} from "constants/salesTaxRegistrationStatusType";

const SALES_TAX_PRODUCT_ID = process.env.PUBLIC_SALES_TAX_PRODUCT_ID!;

const columnHelper = createColumnHelper<Registration>();

export const StatusMap = {
  PENDING_DETAILS: "Pending details",
  IN_REVIEW: "In review",
  PROCESSING: "Processing",
  REGISTERED: "Registered",
};

const StatusBadge = ({ status }: { status: RegistrationStatus }) => {
  return (
    <div
      className={classNames(
        "t-text-text-60 t-max-w-fit t-text-body-sm t-text-center t-px-2 t-py-1 t-text-nowrap t-rounded-md",
        {
          "t-bg-red-10 ": status === PENDING_DETAILS,
        },
        {
          "t-bg-blue-10 t-text-body-sm":
            status === IN_REVIEW || status === PROCESSING,
        },
        {
          "t-bg-green-20": status === REGISTERED,
        }
      )}
    >
      <span>{StatusMap[status]}</span>
    </div>
  );
};

const getToolTipContent = (status: RegistrationStatus) => {
  switch (status) {
    case PENDING_DETAILS:
      return (
        <p className="t-m-0">Enter any existing tax IDs for this state.</p>
      );
    case IN_REVIEW:
      return (
        <p className="t-m-0">
          Reviewing your info before <br /> starting registration.
        </p>
      );
    case PROCESSING:
      return (
        <p className="t-m-0">
          Application submitted! License arrives <br /> at your mailing address
          in 1-2 weeks.
        </p>
      );
    case REGISTERED:
      return (
        <p className="t-m-0">
          Completed! <br />
          You’re ready to file.
        </p>
      );
    default:
      return <p></p>;
  }
};

export const getSelectedState = (
  stateId: string,
  states?: { label: string; value: string }[]
) => {
  const selectedState = states?.find((state) => state.value === stateId);
  return selectedState ? selectedState.label : "";
};

export type initialRegistrationDataType = {
  state_registration_id: string;
  state: string;
  state_id: string;
  status?: string;
};

export const paymentScheduleOption = [
  { label: "Monthly", value: MONTHLY },
  { label: "Quarterly", value: QUARTERLY },
  { label: "Annually", value: ANNUALLY },
  { label: "Semi-Annually", value: SEMI_ANNUALLY },
];

export const SalesTaxRegistrations = () => {
  const [isRegistrationTypeModalOpen, setIsRegistrationTypeModalOpen] =
    useState(false);
  const [initialRegistrationData, setInitialRegistrationData] =
    useState<initialRegistrationDataType>({
      state_registration_id: "",
      state: "",
      state_id: "",
      status: "",
    });
  const [isAddRegistrationModalOpen, setIsAddRegistrationModalOpen] =
    useState(false);
  const [
    isAddExistingRegistrationModalOpen,
    setIsAddExistingRegistrationModalOpen,
  ] = useState(false);
  const query = useQuery();
  const { update } = useUpdateQuery();
  const stateId = query.get("state_id");
  const type = query.get("type");
  const { isAdmin } = useRoleBasedView();
  const entityId = useCurrentEntityId();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isBusinessDetailsModalOpen, setIsBusinessDetailsModalOpen] =
    useState(false);

  const {
    data: registrations,
    isLoading,
    isSuccess,
  } = useGetSalesTaxRegistrationsQuery({
    entityId,
  });
  const [createOnboarding, { isLoading: isCreatingOnboarding }] =
    useCreateEntityProductOnboardingMutation();

  const { alertToast, infoToast } = useToast();

  const { uuid: groupId } = useCurrentGroupContext();

  const {
    data: products,
    isLoading: isLoadingProducts,
    isSuccess: isSuccessProducts,
  } = useGetEntityOnboardingProductsQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );
  const { data: countries } = useGetCountriesDataQuery();

  const salesTaxOnboarding = products?.find(
    (p) => p.name === PRODUCT_ONBOARDING.SALES_TAX
  )!;

  const unitatedStates = countries?.find(
    (country) => country.name === "United States"
  )?.states;

  const stateOptions = unitatedStates
    ?.filter((state) => {
      return !registrations?.some(
        (registration) => registration.state_id == state.uuid
      );
    })
    .map((state) => ({
      label: state.name,
      value: state.uuid,
    }));

  const handleRegistrationFromQuery = () => {
    if (!isSalesTaxOnboardingCompleted) {
      alertToast({
        message: "Provide business details to get started ",
      });
      return;
    }

    const registeredState = registrations?.find(
      (registration) => registration.state_id === stateId
    );
    if (registeredState && registeredState.status !== PENDING_DETAILS) {
      return;
    }
    const selectedState = unitatedStates?.find(
      (state) => state.uuid === stateId
    );
    if (!selectedState) {
      return;
    }
    setInitialRegistrationData({
      state: selectedState.name,
      state_id: stateId || "",
      state_registration_id: registeredState?.state_registration_id || "",
    });

    if (type === "new") {
      setIsAddRegistrationModalOpen(true);
      setIsRegistrationTypeModalOpen(false);
    } else if (type === "existing") {
      setIsAddExistingRegistrationModalOpen(true);
      setIsRegistrationTypeModalOpen(false);
    }
  };

  const handleRegistrationModalClose = (
    type: "new" | "existing" | "type" | "details"
  ) => {
    setInitialRegistrationData({
      state_registration_id: "",
      state: "",
      state_id: "",
      status: "",
    });
    update({
      query: "state_id",
      value: null,
    });
    update({
      query: "type",
      value: null,
    });

    setIsRegistrationTypeModalOpen(false);

    switch (type) {
      case "new":
        return setIsAddRegistrationModalOpen(false);
      case "existing":
        return setIsAddExistingRegistrationModalOpen(false);
      case "type":
        update({
          query: "state_id",
          value: null,
        });
        update({
          query: "type",
          value: null,
        });
        setIsRegistrationTypeModalOpen(false);
        return;
      case "details":
        update({
          query: "state_registration_id",
          value: null,
        });
        setIsDetailsModalOpen(false);
        return;
      default:
        return;
    }
  };

  const handleRowClick = (rowData: Registration) => {
    const status = rowData.status;

    if (status === PENDING_DETAILS) {
      update({
        query: "state_id",
        value: rowData.state_id,
      });
      setIsRegistrationTypeModalOpen(true);
    }
    if (status === REGISTERED && !isAdmin) {
      update({
        query: "state_registration_id",
        value: rowData.state_registration_id,
      });
      setIsDetailsModalOpen(true);
    }

    if (status === PROCESSING) {
      setInitialRegistrationData({
        state_registration_id: rowData.state_registration_id,
        state: rowData.state,
        state_id: rowData.state_id,
        status: rowData.status,
      });
      setIsAddExistingRegistrationModalOpen(true);
    }

    if (status === IN_REVIEW || (status === REGISTERED && isAdmin)) {
      if (isAdmin) {
        setInitialRegistrationData({
          state_registration_id: rowData.state_registration_id,
          state: rowData.state,
          state_id: rowData.state_id,
          status: rowData.status,
        });
        setIsAddExistingRegistrationModalOpen(true);
        return;
      }
      infoToast({
        title: "Registration in review",
        message: "Reviewing your info before starting registration.",
      });
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("state", {
        header: "State",
        cell: (info) => info.getValue(),
        size: 30,
      }),
      columnHelper.accessor("request_date", {
        header: "Request date",
        cell: (info) =>
          info.getValue() ? dayjs(info.getValue()).format(DISPLAY_DATE) : "-",
        size: 30,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="t-text-body ">
              <ConditionalToolTip
                side="left"
                text={getToolTipContent(value as RegistrationStatus)}
                condition={value}
              >
                <div>{value ? <StatusBadge status={value} /> : "-"}</div>
              </ConditionalToolTip>
            </span>
          );
        },
        size: 30,
      }),
      columnHelper.accessor("tax_detail_id", {
        header: "",
        cell: (info) => {
          const status = info.row.original.status;

          return (
            <div className="t-w-full t-flex t-justify-end">
              <span className="t-text-body-sm t-text-nowrap t-text-text-30 t-cursor-pointer">
                {status === PENDING_DETAILS ? "Add Details" : "View details"}
              </span>
            </div>
          );
        },
        size: 10,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: registrations ? registrations : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const dispatch = useDispatch();

  const isSalesTaxOnboardingCompleted = salesTaxOnboarding?.progress === 100;

  const handleOnboarding = async () => {
    if (!salesTaxOnboarding) {
      await createOnboarding({
        productId: SALES_TAX_PRODUCT_ID,
        groupId,
        entityId,
      }).unwrap();
    }
    dispatch(
      openOnboardingWidget({
        product: PRODUCT_ONBOARDING.SALES_TAX,
        entityId,
      })
    );
  };

  useMemo(() => {
    if (isSuccess && stateId && isSalesTaxOnboardingCompleted) {
      handleRegistrationFromQuery();
    }
  }, [
    stateId,
    type,
    isSuccess,
    isSalesTaxOnboardingCompleted,
    registrations,
    unitatedStates,
  ]);

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Registrations"
          right={
            <div className="t-flex gap-0">
              {isSalesTaxOnboardingCompleted && !isLoadingProducts && (
                <Button
                  size="small"
                  customType="secondary"
                  onClick={() => setIsBusinessDetailsModalOpen(true)}
                >
                  Update details
                </Button>
              )}
              {Boolean(isSalesTaxOnboardingCompleted && !isLoadingProducts) && (
                <Dropdown.Root>
                  <Dropdown.Trigger
                    asChild
                    className="t-group t-text-text-60 t-ml-2.5"
                  >
                    <div className="t-flex t-items-center t-gap-2.5">
                      <Button size="small" customType="primary">
                        Add Registration
                        <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-text-surface">
                          <ArrowDown color="currentColor" strokeWidth="1.5" />
                        </div>
                      </Button>
                    </div>
                  </Dropdown.Trigger>
                  <Dropdown.Portal>
                    <Dropdown.Content
                      sideOffset={8}
                      side="bottom"
                      className="t-w-10"
                      align="end"
                    >
                      <Dropdown.Item
                        onSelect={() => setIsAddRegistrationModalOpen(true)}
                      >
                        <div className="t-flex t-items-center t-gap-1">
                          <PlusIcon size="12" />
                          <div>Add new</div>
                        </div>
                      </Dropdown.Item>
                      <Dropdown.Item
                        onSelect={() =>
                          setIsAddExistingRegistrationModalOpen(true)
                        }
                      >
                        <div className="t-flex t-items-center t-gap-1">
                          <AddExistingIcon />
                          <div>Add existing</div>
                        </div>
                      </Dropdown.Item>
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
              )}
            </div>
          }
        />
      }
    >
      {Boolean(!isSalesTaxOnboardingCompleted && isSuccessProducts) && (
        <div className="t-flex t-h-32">
          <OnboardingHomeCard
            type="sales_tax"
            title="Add business details"
            progress={salesTaxOnboarding?.progress || 0}
            onClick={handleOnboarding}
            isLoading={isCreatingOnboarding}
          />
        </div>
      )}
      <BusinessDetailsModal
        open={isBusinessDetailsModalOpen}
        onClose={() => setIsBusinessDetailsModalOpen(false)}
      />
      <AddRegistrationModal
        open={isAddRegistrationModalOpen}
        stateOptions={stateOptions}
        onClose={() => handleRegistrationModalClose("new")}
        initialRegistrationData={initialRegistrationData}
      />
      {isAddExistingRegistrationModalOpen && (
        <AddExistingRegistrationModal
          stateOptions={stateOptions}
          initialRegistrationData={initialRegistrationData}
          onClose={() => handleRegistrationModalClose("existing")}
        />
      )}
      <RegistrationTypeModal
        open={isRegistrationTypeModalOpen}
        stateOptions={stateOptions}
        onModalClose={() => handleRegistrationModalClose("type")}
      />
      <RegistrationDetails
        open={isDetailsModalOpen}
        onClose={() => handleRegistrationModalClose("details")}
      />

      <Async.Root
        isLoading={isLoading || isLoadingProducts}
        isEmpty={
          Boolean(!registrations?.length) || !isSalesTaxOnboardingCompleted
        }
        isSuccess={isSuccess}
      >
        <Async.Empty>
          <div className="t-h-full t-flex t-flex-col t-items-center t-justify-center">
            <div>
              {isSalesTaxOnboardingCompleted ? (
                <EmptySalesTax />
              ) : (
                <SalesTaxRegistrationPending />
              )}
            </div>
            <p className="t-text-text-100 t-text-body-sm t-mb-0 t-mt-6">
              {isSalesTaxOnboardingCompleted
                ? "No sales tax registration added yet"
                : "Finish setting up your registration dashboard "}
            </p>
            <p className="t-text-text-30 t-text-body-sm t-mb-0 t-mt-1">
              {isSalesTaxOnboardingCompleted
                ? "Start by adding a new registration or linking your existing one."
                : "Provide business details to get started "}
            </p>
          </div>
        </Async.Empty>
        <Async.Success>
          <DashboardContainer>
            <DashboardContainer.Content>
              <div className="t-pt-4">
                <TableUI
                  sticky={false}
                  onRowClick={(row) => handleRowClick(row.original)}
                  table={table}
                  size="regular"
                />
              </div>
            </DashboardContainer.Content>
          </DashboardContainer>
        </Async.Success>
      </Async.Root>
    </DashboardLayout>
  );
};
